/**
 * RTL infrastructure — SKNOTIFICATION-GLOBAL-CR-001 UICC-001 §7/§8 ("kit
 * adoption": RTL grid mirroring, LTR-isolated placeholders in an RTL
 * preview). No `@softknack/globalization-kit` package exists to pull this
 * from (confirmed absent — see prior investigation), so this is a small,
 * local equivalent, scoped to what this app actually needs: UAE (Arabic)
 * staff using the console.
 *
 * Approach: React Native's logical style properties (marginStart/marginEnd,
 * paddingStart/paddingEnd, start/end for absolute positioning, textAlign:
 * 'auto') already auto-mirror based on I18nManager.isRTL, on native AND on
 * react-native-web (pure CSS logical properties under the hood — no native
 * reload requirement, since this console is web-primary). So the fix for
 * "~75 physical properties" is real property changes in the screens, not a
 * runtime style-flip layer here.
 */
import { useEffect } from 'react';
import { I18nManager } from 'react-native';
import { useApp } from '../context/appContext';

/** UAE staff is the one real RTL case this CR's own scope names (Dubai/India). */
export function isRTLLocale(locale: string): boolean {
  return /^ar/i.test(locale);
}

/**
 * Wraps a substring that must keep its own (LTR) internal ordering even
 * inside RTL-flowing text — a phone number, a percentage, a placeholder
 * value — using Unicode FSI/PDI isolates (U+2066/U+2069) rather than the
 * legacy LRM/RLM marks, which only bias neighbouring characters and don't
 * actually contain a run's directionality.
 */
export function isolateLTR(text: string): string {
  return `⁦${text}⁩`;
}

/** Current console direction, derived from AppContext.locale (not the recipient's message locale — a separate concept, see schema/notification's per-recipient locale). */
export function useDirection(): { isRTL: boolean; dir: 'ltr' | 'rtl' } {
  const { locale } = useApp();
  const isRTL = isRTLLocale(locale);
  return { isRTL, dir: isRTL ? 'rtl' : 'ltr' };
}

/**
 * Applies the direction to I18nManager whenever the console locale changes.
 * Mount once near the app root (see App.tsx). On react-native-web this
 * takes effect on the next render (CSS logical properties, no native
 * reload); kept as an effect (not a render-time call) because I18nManager
 * is a module-level side effect, not something to invoke during render.
 */
export function useApplyDirection(): void {
  const { isRTL } = useDirection();
  useEffect(() => {
    if (I18nManager.isRTL !== isRTL) {
      I18nManager.allowRTL(isRTL);
      I18nManager.forceRTL(isRTL);
    }
  }, [isRTL]);
}
